// src/utils/fcm_bridge.ts
declare global {
  interface Window {
    flutter_inappwebview?: { callHandler: (name: string, ...args: any[]) => Promise<any> };
    HostApp?: { postMessage: (msg: string) => void };
    onNativePushToken?: (t: string | null) => void;
    onNativePushTokenRefresh?: (t: string | null) => void;
  }
}

type Handler = (t: string | null) => void;

let currentToken: string | null = null;
const listeners: Handler[] = [];

function notify(t: string | null) {
  for (const h of listeners) h(t);
}

function setToken(t: string | null) {
  if (t && typeof t === 'string') {
    currentToken = t;          // only set when we have a real string
    notify(currentToken);
  }
}

export function onPushToken(h: Handler) {
  listeners.push(h);
  if (currentToken) h(currentToken);
}

// Wire native callbacks (don’t overwrite with null)
const prevOnTok = window.onNativePushToken;
window.onNativePushToken = (t) => {
  prevOnTok?.(t);
  setToken(t);
};
const prevOnTokRef = window.onNativePushTokenRefresh;
window.onNativePushTokenRefresh = (t) => {
  prevOnTokRef?.(t);
  setToken(t);
};

async function withTimeout<T>(p: Promise<T>, ms = 1500): Promise<T> {
  return await Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

// src/utils/fcm_bridge.ts  (additions)

export function getCachedPushToken(): string | null {
  return currentToken;
}

export async function requestPushToken(): Promise<string | null> {
  if (currentToken) {
    console.log('[fcm_bridge] returning cached', currentToken);
    return currentToken;
  }
  try {
    if (window.flutter_inappwebview?.callHandler) {
      const t = await withTimeout(window.flutter_inappwebview.callHandler('getPushToken'), 2000);
      console.log('[fcm_bridge] callHandler returned:', t);
      if (t && typeof t === 'string') setToken(t);
      if (!currentToken) {
        // wait briefly for native push if it races
        const waited = await withTimeout(new Promise<string | null>((resolve) => {
          const once: Handler = (tt) => {
            const i = listeners.indexOf(once); if (i >= 0) listeners.splice(i, 1);
            resolve(typeof tt === 'string' ? tt : null);
          };
          listeners.push(once);
        }), 3000).catch(() => null);
        if (waited) setToken(waited);
      }
      return currentToken;
    }

    if (window.HostApp?.postMessage) {
      const waiter = new Promise<string | null>((resolve) => {
        const once: Handler = (tt) => {
          const i = listeners.indexOf(once); if (i >= 0) listeners.splice(i, 1);
          resolve(typeof tt === 'string' ? tt : null);
        };
        listeners.push(once);
      });
      window.HostApp.postMessage(JSON.stringify({ type: 'REQUEST_PUSH_TOKEN' }));
      const t = await withTimeout(waiter, 2000).catch(() => null);
      console.log('[fcm_bridge] HostApp waiter got:', t);
      if (t) setToken(t);
      return currentToken;
    }

    return currentToken;
  } catch (e) {
    console.log('[fcm_bridge] error:', e);
    return currentToken;
  }
}
